import React, { useCallback, useEffect, useState } from "react";
import useFetchAndTrace from "../../../../../utils/hooks/fetchWithTrace";
import settings from "../../../../../constants/settings";
import { useAppDispatch } from "../../../../../utils/hooks/store";
import {
  BillableElement,
  BillOfMaterials,
  Board,
  CADSources,
  ElementPlacement,
  pageFailure,
} from "../../../../../reducers/uploadedBoard";
import {
  ChangeDetail,
  ChangeName,
  DescriptionText,
  OptionsContainer,
  StagedChangesContainer,
  StagedChangesHeader,
  StagedChangesList,
  To,
} from "./CopyFromBoardStyles";
import _ from "lodash";
import { useBoardFromContext } from "../../../../../utils/hooks/commonly-stored/uploaded-board/board";
import Button from "../../../../common/Button";
import ModalContentWithCloseButton from "../../../../common/ModalContentWithCloseButton";
import LoadingIcon from "../../../../common/LoadingIcon";
import Checkbox from "../../../../common/Checkbox";
import SelectBoard from "../../../../SelectBoard";
import usePart from "../../../../../utils/hooks/commonly-stored/parts";
import {
  useAddBoardElement,
  useBillOfMaterials,
  useUpdateBoardElementSelection,
} from "../../../../../utils/hooks/commonly-stored/uploaded-board/updates/billOfMaterials";

interface ElementState {
  doNotPopulate: boolean;
  part_id: string | null;
  isConsignment: boolean;
  lockAllocation: boolean;
  elementPlacement: ElementPlacement | null;
}

interface StagedChange {
  elementIdToUpdate: string;
  name: string;
  currentState: ElementState;
  stagedState: ElementState;
}

interface StagedAddition {
  name: string;
  valueText: string;
  stagedState: ElementState;
}

interface StagedUpdates {
  changes: StagedChange[];
  additions: StagedAddition[];
}

function PartState({ state }: { state: ElementState }) {
  const { part, loading } = usePart(state.part_id);
  if (loading) {
    return <LoadingIcon text={"Loading part..."} />;
  }
  if (part === null) {
    return "No part selected";
  }
  return part.name;
}

const isConsignedToString = (state: ElementState) => {
  return state.isConsignment ? "Provided by customer" : "Provided by FlashPCB";
};

const dnpStateToString = (state: ElementState) => {
  return state.doNotPopulate ? "DNP" : "Populate";
};

const placementStateToString = (state: ElementState) => {
  if (state.elementPlacement === null) {
    return "No placement info";
  }
  const x = state.elementPlacement.x.toLocaleString(undefined, {
    maximumFractionDigits: 2,
  });
  const y = state.elementPlacement.y.toLocaleString(undefined, {
    maximumFractionDigits: 2,
  });
  const unit = state.elementPlacement.unit;
  const rotation = state.elementPlacement.rotation_degrees;
  const mountType = state.elementPlacement.mount_type;
  return `${x} ${unit} ${y} ${unit}, ${rotation}°, ${mountType}`;
};

function StagedChangesView({
  stagedUpdates,
}: {
  stagedUpdates: StagedUpdates | null;
}) {
  if (stagedUpdates === null) {
    return <LoadingIcon text={"Calculating changes"} />;
  }
  if (
    stagedUpdates.changes.length == 0 &&
    stagedUpdates.additions.length == 0
  ) {
    return <>No changes to apply</>;
  }

  const to = <To>→</To>;

  return (
    <>
      {stagedUpdates.changes.length > 0 && (
        <>
          <StagedChangesHeader>Staged changes:</StagedChangesHeader>
          <StagedChangesList>
            {stagedUpdates.changes.map((change) => {
              return (
                <div key={JSON.stringify(change)}>
                  <ChangeName>{change.name}:</ChangeName>
                  {change.currentState.part_id !=
                    change.stagedState.part_id && (
                    <ChangeDetail>
                      <PartState state={change.currentState} /> {to}{" "}
                      <PartState state={change.stagedState} />
                    </ChangeDetail>
                  )}
                  {change.currentState.isConsignment !=
                    change.stagedState.isConsignment && (
                    <ChangeDetail>
                      {isConsignedToString(change.currentState)} {to}{" "}
                      {isConsignedToString(change.stagedState)}
                    </ChangeDetail>
                  )}
                  {change.currentState.doNotPopulate !=
                    change.stagedState.doNotPopulate && (
                    <ChangeDetail>
                      {dnpStateToString(change.currentState)} {to}{" "}
                      {dnpStateToString(change.stagedState)}
                    </ChangeDetail>
                  )}
                  {!_.isEqual(
                    change.currentState.elementPlacement,
                    change.stagedState.elementPlacement,
                  ) &&
                    change.stagedState.elementPlacement !== null && (
                      <ChangeDetail>
                        {placementStateToString(change.currentState)} {to}{" "}
                        {placementStateToString(change.stagedState)}
                      </ChangeDetail>
                    )}
                </div>
              );
            })}
          </StagedChangesList>
        </>
      )}
      {stagedUpdates.additions.length > 0 && (
        <>
          <StagedChangesHeader>Staged additions:</StagedChangesHeader>
          <StagedChangesList>
            {stagedUpdates.additions.map((addition) => {
              return (
                <div key={JSON.stringify(addition)}>
                  <ChangeName>{addition.name}:</ChangeName>
                  {addition.stagedState.part_id !== null && (
                    <>
                      <ChangeDetail>
                        <PartState state={addition.stagedState} />
                      </ChangeDetail>
                      <ChangeDetail>
                        {dnpStateToString(addition.stagedState)}
                      </ChangeDetail>
                      <ChangeDetail>
                        {isConsignedToString(addition.stagedState)}
                      </ChangeDetail>
                    </>
                  )}
                  {addition.stagedState.elementPlacement !== null && (
                    <ChangeDetail>
                      {placementStateToString(addition.stagedState)}
                    </ChangeDetail>
                  )}
                </div>
              );
            })}
          </StagedChangesList>
        </>
      )}
    </>
  );
}

export default function CopyFromBoard({ close }: { close: () => void }) {
  const dispatch = useAppDispatch();
  const board = useBoardFromContext().board as Board;
  const isGerberUpload = board.cad_source == CADSources.GERBER;
  const billOfMaterials = useBillOfMaterials() as BillOfMaterials;
  const fetchAndTrace = useFetchAndTrace();
  const [selectedBoard, setSelectedBoard] = useState<Board | null>(null);
  const [selectedBOM, setSelectedBOM] = useState<BillOfMaterials | null>(null);
  const [stagedUpdates, setStagedUpdates] = useState<StagedUpdates | null>(
    null,
  );
  const updateBoardElementSelection = useUpdateBoardElementSelection();
  const addBoardElement = useAddBoardElement();

  const [matchValueText, setMatchValueText] = useState(true);
  const [matchPackageName, setMatchPackageName] = useState(true);
  const [matchMPN, setMatchMPN] = useState(false);

  const [addMissingElements, setAddMissingElements] = useState(true);
  const [copyPartAndDNPData, setCopyPartAndDNPData] = useState(true);
  const [copyPlacementData, setCopyPlacementData] = useState(
    billOfMaterials.elements.length === 0,
  );

  const errorHandler = useCallback(() => {
    dispatch(pageFailure({ boardId: board.id }));
  }, [dispatch, board.id]);

  const getStagedState = useCallback(
    (
      referenceElement: BillableElement,
      matchingElement?: BillableElement,
    ): ElementState => {
      const elementPlacement = copyPlacementData
        ? referenceElement.placement
        : null;
      if (copyPartAndDNPData) {
        return {
          doNotPopulate: referenceElement.do_not_populate,
          part_id: referenceElement.part_id,
          isConsignment: referenceElement.is_consignment,
          lockAllocation: referenceElement.lock_allocation,
          elementPlacement,
        };
      } else {
        const hasDefault = matchingElement !== undefined;
        return {
          doNotPopulate: hasDefault ? matchingElement.do_not_populate : false,
          part_id: hasDefault ? matchingElement.part_id : null,
          isConsignment: hasDefault ? matchingElement.is_consignment : false,
          lockAllocation: hasDefault ? matchingElement?.lock_allocation : false,
          elementPlacement,
        };
      }
    },
    [copyPartAndDNPData, copyPlacementData],
  );

  const matchesBasedOnSelectedOptions = useCallback(
    (referenceElement: BillableElement, matchingElement: BillableElement) => {
      if (
        matchValueText &&
        referenceElement.value_text != matchingElement.value_text
      ) {
        return false;
      }
      if (
        matchPackageName &&
        referenceElement.footprint_name != matchingElement.footprint_name
      ) {
        return false;
      }
      if (
        isGerberUpload &&
        matchMPN &&
        referenceElement.part_specification?.mpn !=
          matchingElement.part_specification?.mpn
      ) {
        return false;
      }
      return true;
    },
    [isGerberUpload, matchMPN, matchPackageName, matchValueText],
  );

  const hasUpdate = useCallback(
    (referenceElement: BillableElement, matchingElement: BillableElement) => {
      const hasPartAndDNPDataUpdate =
        copyPartAndDNPData &&
        (referenceElement.do_not_populate != matchingElement.do_not_populate ||
          referenceElement.part_id != matchingElement.part_id ||
          referenceElement.is_consignment != matchingElement.is_consignment);
      // Only update placement if the reference element actually has placement info.
      const hasPlacementDataUpdate =
        copyPlacementData &&
        referenceElement.placement !== null &&
        !_.isEqual(referenceElement.placement, matchingElement.placement);
      return hasPartAndDNPDataUpdate || hasPlacementDataUpdate;
    },
    [copyPartAndDNPData, copyPlacementData],
  );

  useEffect(() => {
    if (selectedBOM !== null) {
      setStagedUpdates(
        selectedBOM.elements.reduce(
          (acc, referenceElement) => {
            const matchingElement = billOfMaterials.elements.find((element) => {
              return element.name == referenceElement.name;
            });
            if (matchingElement === undefined) {
              if (addMissingElements) {
                acc.additions.push({
                  name: referenceElement.name,
                  valueText: referenceElement.value_text,
                  stagedState: getStagedState(referenceElement),
                });
              }
            } else {
              if (
                matchesBasedOnSelectedOptions(
                  referenceElement,
                  matchingElement,
                ) &&
                hasUpdate(referenceElement, matchingElement)
              ) {
                acc.changes.push({
                  elementIdToUpdate: matchingElement.id,
                  name: matchingElement.name,
                  currentState: {
                    doNotPopulate: matchingElement.do_not_populate,
                    part_id: matchingElement.part_id,
                    isConsignment: matchingElement.is_consignment,
                    lockAllocation: referenceElement.lock_allocation,
                    elementPlacement: matchingElement.placement,
                  },
                  stagedState: getStagedState(
                    referenceElement,
                    matchingElement,
                  ),
                });
              }
            }
            return acc;
          },
          { changes: [], additions: [] } as StagedUpdates,
        ),
      );
    }
  }, [
    addMissingElements,
    billOfMaterials.elements,
    getStagedState,
    hasUpdate,
    matchesBasedOnSelectedOptions,
    selectedBOM,
  ]);

  const handleLoadBoard = useCallback(async (response: Response) => {
    if (response.ok) {
      const billOfMaterialsResponse =
        (await response.json()) as BillOfMaterials;
      setSelectedBOM(billOfMaterialsResponse);
    } else {
      throw Error("Failed to load BOM for copy");
    }
  }, []);

  useEffect(() => {
    setStagedUpdates(null);
    if (selectedBoard !== null) {
      void fetchAndTrace(
        `${settings.api_url}/boards/${selectedBoard.id}/bill-of-materials`,
        handleLoadBoard,
        errorHandler,
      );
    }
  }, [errorHandler, fetchAndTrace, handleLoadBoard, selectedBoard]);

  const handleClick = () => {
    if (stagedUpdates !== null) {
      if (stagedUpdates.changes.length > 0) {
        updateBoardElementSelection(
          stagedUpdates.changes.map((change) => {
            return {
              elementName: change.name,
              elementId: change.elementIdToUpdate,
              doNotPopulate: change.stagedState.doNotPopulate,
              part_id: change.stagedState.part_id,
              isConsignment: change.stagedState.isConsignment,
              lockAllocation: change.stagedState.lockAllocation,
              elementPlacement: change.stagedState.elementPlacement,
            };
          }),
          stagedUpdates.additions.length > 0 ? undefined : close,
        );
      }
      if (stagedUpdates.additions.length > 0) {
        addBoardElement(
          stagedUpdates.additions.map((addition) => {
            return {
              name: addition.name,
              valueText: addition.valueText,
              doNotPopulate: addition.stagedState.doNotPopulate,
              part_id: addition.stagedState.part_id,
              isConsignment: addition.stagedState.isConsignment,
              lockAllocation: addition.stagedState.lockAllocation,
              elementPlacement: addition.stagedState.elementPlacement,
            };
          }),
          close,
        );
      }
    }
  };

  return (
    <ModalContentWithCloseButton
      closeModal={close}
      title={"Copy bill of materials from board"}
      fullScreen
    >
      <SelectBoard
        selectedBoard={selectedBoard}
        setSelectedBoard={setSelectedBoard}
        doNotIncludeBoardId={board.id}
        placeholder={"Search for a board..."}
      />
      {selectedBoard !== null && (
        <>
          <DescriptionText>
            Copy from elements with matching reference designators and whose
            following values match:
          </DescriptionText>
          <OptionsContainer>
            <Checkbox
              checked={matchValueText}
              onChange={() => setMatchValueText((prevState) => !prevState)}
              label="Value text"
            />
            <Checkbox
              checked={matchPackageName}
              onChange={() => setMatchPackageName((prevState) => !prevState)}
              label={"Footprint name"}
            />
            {isGerberUpload && (
              <Checkbox
                checked={matchMPN}
                onChange={() => setMatchMPN((prevState) => !prevState)}
                label={"Part number specified in BOM"}
              />
            )}
          </OptionsContainer>

          <DescriptionText>Copy options:</DescriptionText>
          <OptionsContainer>
            <Checkbox
              checked={addMissingElements}
              onChange={() => setAddMissingElements((prevState) => !prevState)}
              label="Add missing elements"
            />
            <Checkbox
              checked={copyPartAndDNPData}
              onChange={() => setCopyPartAndDNPData((prevState) => !prevState)}
              label="Copy part and DNP data"
            />
            <Checkbox
              checked={copyPlacementData}
              onChange={() => setCopyPlacementData((prevState) => !prevState)}
              label="Copy part placement data"
            />
          </OptionsContainer>
          <StagedChangesContainer>
            <StagedChangesView stagedUpdates={stagedUpdates} />
          </StagedChangesContainer>
        </>
      )}
      <Button
        styleProps={{ margin: "auto", marginTop: "1rem" }}
        label={"Copy BOM"}
        disabled={
          stagedUpdates === null ||
          (stagedUpdates.changes.length == 0 &&
            stagedUpdates.additions.length == 0)
        }
        onClick={handleClick}
      />
    </ModalContentWithCloseButton>
  );
}
